import { Avatar, Box, Divider, Tooltip, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import { ClinicalRecord } from "../../hooks/useClinicalRecords";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";
import { formatRecordDate, htmlToPlainText } from "./utils";

export default function RecordCard({
  record,
  typeLabel,
  noContentLabel,
  onClick
}: {
  record: ClinicalRecord;
  typeLabel: string;
  noContentLabel: string;
  onClick: () => void;
}) {
  const { t, i18n } = useTranslation();
  const plainText = htmlToPlainText(record.content);

  return (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: semanticColors.surface.canvas,
        borderRadius: "16px",
        boxShadow: "0px 0px 6px rgba(30,39,79,0.09)",
        overflow: "hidden",
        cursor: "pointer",
        transition: "box-shadow 200ms ease",
        "&:hover": { boxShadow: "0px 0px 14px rgba(30,39,79,0.16)" }
      }}
    >
      <Box sx={{ px: "20px", pt: "16px", pb: "12px" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <Avatar
            src={record.doctorPictureUrl}
            alt={record.doctorName}
            sx={{
              width: 44,
              height: 44,
              border: `1.5px solid ${semanticColors.border.middle}`,
              flexShrink: 0
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                ...typography.body.lBold,
                color: semanticColors.text.primary
              }}
            >
              {record.doctorName}
            </Typography>
            {record.doctorSpecialty ? (
              <Typography
                sx={{
                  ...typography.body.m,
                  color: semanticColors.text.secondary
                }}
              >
                {record.doctorSpecialty}
              </Typography>
            ) : null}
          </Box>
          <Tooltip
            title={
              record.visibleToPatient
                ? t("clinicalRecords.visibleToPatient")
                : t("clinicalRecords.notVisibleToPatient")
            }
          >
            <Box
              sx={{ display: "flex", alignItems: "center", px: "4px" }}
              onClick={e => e.stopPropagation()}
            >
              {record.visibleToPatient ? (
                <VisibilityIcon
                  sx={{ fontSize: 18, color: semanticColors.action.primary }}
                />
              ) : (
                <VisibilityOffIcon
                  sx={{ fontSize: 18, color: semanticColors.text.subtle }}
                />
              )}
            </Box>
          </Tooltip>
        </Box>

        <Box
          sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "10px" }}
        >
          <Typography
            sx={{
              ...typography.body.mBold,
              color: semanticColors.text.secondary
            }}
          >
            {typeLabel}
          </Typography>
          <Box
            sx={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              bgcolor: semanticColors.text.subtle
            }}
          />
          <Typography
            sx={{ ...typography.body.m, color: semanticColors.text.secondary }}
          >
            {formatRecordDate(record.createdAt, i18n.language)}
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: "20px", py: "14px" }}>
        {plainText ? (
          <Box
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(record.content)
            }}
            sx={{
              ...typography.body.l,
              color: semanticColors.text.primary,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              "& b, & strong": { fontWeight: 700 },
              "& i, & em": { fontStyle: "italic" },
              "& u": { textDecoration: "underline" },
              "& s": { textDecoration: "line-through" }
            }}
          />
        ) : (
          <Typography
            sx={{
              ...typography.body.l,
              color: semanticColors.text.subtle,
              fontStyle: "italic"
            }}
          >
            {noContentLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
